import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Camera, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { avatarApi } from '@/lib/api';

/**
 * Dashboard hero that shows a looping crossfade morph between the user's uploaded
 * "before" photo and their AI-generated "healthiest self".
 * If the user has not yet generated an avatar, shows a CTA to generate one.
 */
const MorphingAvatarHero = ({ userName }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState(0); // 0 = before, 1 = after

  useEffect(() => {
    avatarApi
      .latest()
      .then(setData)
      .catch(() => setData({ has_avatar: false }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!data?.has_avatar || !data?.source_b64) return;
    const t = setInterval(() => setPhase((p) => (p === 0 ? 1 : 0)), 2600);
    return () => clearInterval(t);
  }, [data]);

  if (loading) {
    return (
      <div
        className="mb-10 rounded-3xl border border-[#E5E1D8] bg-white h-64 animate-pulse"
        data-testid="morphing-avatar-loading"
      />
    );
  }

  const hasBoth = data?.has_avatar && data?.source_b64;
  const hasOnlyAfter = data?.has_avatar && !data?.source_b64;

  // No avatar yet — show CTA
  if (!data?.has_avatar) {
    return (
      <div
        className="mb-10 rounded-3xl border border-[#E5E1D8] bg-gradient-to-br from-[#F2EFE9] to-white p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6"
        data-testid="morphing-avatar-empty"
      >
        <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl bg-gradient-to-br from-[#7E9979] via-[#4A7C59] to-[#234233] flex items-center justify-center relative overflow-hidden shrink-0">
          <motion.div
            className="absolute inset-0 bg-white/10"
            animate={{ opacity: [0.1, 0.35, 0.1] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />
          <Sparkles className="w-12 h-12 text-white relative" strokeWidth={1.2} />
        </div>
        <div className="flex-1 text-center sm:text-left">
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#7E9979]">Your Vision</span>
          <h2 className="mt-2 font-serif-display text-2xl sm:text-3xl text-[#1A2421] font-medium leading-tight">
            See your healthiest self{userName ? `, ${userName}` : ''}.
          </h2>
          <p className="mt-2 text-sm sm:text-base text-[#4A5A53] font-light">
            Upload a photo and watch AI reveal you 6-12 months into your journey.
          </p>
          <Link to="/avatar">
            <Button
              className="mt-4 rounded-full bg-[#4A7C59] hover:bg-[#3E6B4F] text-white gap-2 px-6"
              data-testid="morphing-avatar-generate-cta"
            >
              <Camera className="w-4 h-4" strokeWidth={1.5} /> Generate my avatar
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const afterSrc = `data:${data.mime_type};base64,${data.image_b64}`;
  const beforeSrc = hasBoth ? `data:${data.source_mime};base64,${data.source_b64}` : null;

  return (
    <div
      className="mb-10 rounded-3xl border border-[#E5E1D8] bg-gradient-to-br from-white to-[#F2EFE9] p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6"
      data-testid="morphing-avatar-hero"
    >
      <div className="relative w-40 h-52 sm:w-48 sm:h-60 rounded-2xl overflow-hidden bg-[#F2EFE9] shrink-0 shadow-sm">
        {hasBoth ? (
          <AnimatePresence initial={false} mode="sync">
            {phase === 0 ? (
              <motion.img
                key="before"
                src={beforeSrc}
                alt="Today"
                className="absolute inset-0 w-full h-full object-cover"
                initial={{ opacity: 0, scale: 1.03 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.02 }}
                transition={{ duration: 1.6, ease: 'easeInOut' }}
                data-testid="morphing-avatar-before"
              />
            ) : (
              <motion.img
                key="after"
                src={afterSrc}
                alt="Healthiest self"
                className="absolute inset-0 w-full h-full object-cover"
                initial={{ opacity: 0, scale: 1.03 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.02 }}
                transition={{ duration: 1.6, ease: 'easeInOut' }}
                data-testid="morphing-avatar-after"
              />
            )}
          </AnimatePresence>
        ) : (
          <img src={afterSrc} alt="Healthiest self" className="absolute inset-0 w-full h-full object-cover" />
        )}
        {hasBoth && (
          <div className="absolute bottom-2 left-2 right-2 flex justify-between text-[10px] tracking-[0.2em] uppercase font-semibold">
            <span
              className={`px-2 py-1 rounded-full backdrop-blur-md transition-opacity duration-500 ${phase === 0 ? 'opacity-100 bg-black/40 text-white' : 'opacity-40 bg-white/70 text-[#1A2421]'}`}
            >
              Today
            </span>
            <span
              className={`px-2 py-1 rounded-full backdrop-blur-md transition-opacity duration-500 ${phase === 1 ? 'opacity-100 bg-[#4A7C59] text-white' : 'opacity-40 bg-white/70 text-[#1A2421]'}`}
            >
              Vision
            </span>
          </div>
        )}
      </div>

      <div className="flex-1 text-center sm:text-left">
        <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#7E9979]">Your Vision</span>
        <h2 className="mt-2 font-serif-display text-2xl sm:text-3xl text-[#1A2421] font-medium leading-tight">
          {hasBoth ? 'From today to your healthiest self.' : 'Your healthiest self.'}
        </h2>
        <p className="mt-2 text-sm sm:text-base text-[#4A5A53] font-light max-w-md">
          {hasBoth
            ? 'A living reminder of where you are and where you\'re heading. Every meal, every rep, every night of sleep bridges the gap.'
            : 'Regenerate anytime as you evolve.'}
        </p>
        <div className="mt-4 flex flex-wrap gap-2 justify-center sm:justify-start">
          <Link to="/avatar">
            <Button
              variant="outline"
              className="rounded-full border-[#E5E1D8] hover:border-[#7E9979] gap-2"
              data-testid="morphing-avatar-open-button"
            >
              {hasOnlyAfter ? 'Add a new photo' : 'Regenerate'} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MorphingAvatarHero;
